import FormLabel from '@mui/material/FormLabel'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import { Datepicker, MbscDatepickerChangeEvent } from '@mobiscroll/react'
import '@mobiscroll/react/dist/css/mobiscroll.react.min.css'
import { useTranslate } from '@repo/unicity'
import { useCallback, useMemo, useState } from 'react'
import DropdownIcon from './assets/dropdown.svg?react'

interface ExpDateProps {
    onChange: (value: string) => void
}

const ExpDate = ({ onChange }: ExpDateProps) => {
    const { translate } = useTranslate()
    const [value, setValue] = useState({ month: '', year: '' })
    const [isOpen, setIsOpen] = useState(false)

    // min = Jan 1 of current year: Mobiscroll applies min.month to ALL years, so April min
    // would block February even in 2027. January ensures all months are always selectable.
    const now = useMemo(() => new Date(new Date().getFullYear(), 0, 1), [])
    const until = useMemo(() => new Date(now.getFullYear() + 10, 11, 31), [now])

    // defaultSelection forces internal day=1. Without it, picker uses today's day (e.g. 29),
    // and switching to February overflows: new Date(2027, 1, 29) = March 1, 2027.
    const defaultSelection = useMemo(() => {
        const d = new Date()
        d.setDate(1)
        d.setHours(0, 0, 0, 0)
        return d
    }, [])

    // Pass value as Date with day=1 to prevent JS day overflow when wheel changes month.
    const dateValue = useMemo(() => {
        if (!value.month || !value.year) return null
        return new Date(parseInt(value.year, 10), parseInt(value.month, 10) - 1, 1)
    }, [value.month, value.year])

    // onChange fires when user confirms with OK; valueText is always present for this event
    const handleChange = useCallback(
        (event: MbscDatepickerChangeEvent) => {
            const text = event.valueText
            if (!text) return
            const [month, year] = text.split('/')
            if (month && year) {
                setValue({ month, year })
                onChange(`${month}/${year}`)
            }
        },
        [onChange]
    )

    const handleClose = useCallback(() => {
        setIsOpen(false)
    }, [])

    const handleOpen = useCallback(() => {
        if (!isOpen) {
            setIsOpen(true)
        }
    }, [isOpen])

    const displayValue = value.month && value.year ? `${value.month}/${value.year}` : ''

    return (
        <Stack
            sx={{
                cursor: 'pointer',
                backgroundColor: '#fff',
                position: 'relative',
                height: '60px',
                boxSizing: 'border-box',
                '.mbsc-form-control-wrapper': {
                    '::before,::after': {
                        border: 'none'
                    },
                    borderRadius: '6px',
                    border: '1px solid rgba(226, 232, 240, 1)'
                }
            }}
            onClick={handleOpen}>
            <InputLabelStyled
                sx={
                    !displayValue
                        ? {
                            transform: 'translate(12px, 24px) scale(1)',
                            top: '0px',
                            left: '0px',
                            color: 'rgba(98, 116, 142, 1)',
                            padding: '0px'
                        }
                        : {
                            color: 'rgba(98, 116, 142, 1)',
                            transform: 'translate(4px, 13px) scale(0.75)',
                            top: '0px',
                            left: '0px',
                            backgroundColor: '#fff',
                            padding: '0px'
                        }
                }>
                {translate('exp_date')}
            </InputLabelStyled>
            <Datepicker
                theme="ios"
                themeVariant="light"
                display="center"
                touchUi={true}
                monthText="MM"
                yearText="YYYY"
                controls={['date']}
                dateWheels={'MM YYYY'}
                dateFormat={'MM/YYYY'}
                min={now}
                max={until}
                defaultSelection={defaultSelection}
                isOpen={isOpen}
                onOpen={handleOpen}
                onChange={handleChange}
                onClose={handleClose}
                setText={translate('ok')}
                cancelText={translate('cancel')}
                inputProps={{
                    style: {
                        fontSize: '14px',
                        color: displayValue ? 'rgba(0, 59, 111, 1)' : 'transparent',
                        fontFamily: 'var(--font-inter)',
                        padding: '30px 40px 8px 12px',
                        backgroundColor: 'transparent',
                        height: '60px',
                        boxSizing: 'border-box',
                        border: 'none'
                    },
                    type: 'tel'
                }}
                value={dateValue}
            />
            {/* Dropdown Icon */}
            <Box
                sx={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                <DropdownIcon />
            </Box>
        </Stack>
    )
}

const InputLabelStyled = styled(FormLabel)(() => ({
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '12px',
    color: 'rgba(0, 59, 111, 1)',
    paddingLeft: '14px',
    zIndex: 9,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    position: 'absolute',
    transition: 'all 0.2s ease-in-out',
    fontFamily: 'var(--font-inter)'
}))

export default ExpDate
